'use client';

import Link from 'next/link';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';

interface BannerData {
  id: string;
  title: string;
  subtitle?: string | null;
  ctaText?: string | null;
  ctaUrl?: string | null;
  bgColor?: string | null;
  bgGradient?: string | null;
  imageUrl?: string | null;
  textColor: string;
  imageOnly?: boolean;
  type?: 'STANDARD' | 'COUNTDOWN';
  targetDate?: string | null;
}

interface BannerStripProps {
  banner: BannerData;
  dismissible?: boolean;
}

/**
 * A slim announcement strip banner — typically used at LANDING_TOP.
 * Rendered server-side; dismissal is client-side via JS (progressive enhancement).
 */
export function BannerStrip({ banner, dismissible = true }: BannerStripProps) {
  const t = useTranslations('bannerStrip');
  const bgStyle: React.CSSProperties = banner.imageUrl
    ? { backgroundImage: `url(${banner.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : banner.bgGradient
    ? { background: banner.bgGradient }
    : { backgroundColor: banner.bgColor ?? '#0f766e' };

  const color = banner.textColor ?? '#ffffff';

  return (
    <div
      id={`banner-${banner.id}`}
      className="relative w-full py-2.5 px-10 text-center text-sm"
      style={bgStyle}
    >
      {banner.imageUrl && !banner.imageOnly && <div className="absolute inset-0 bg-black/30" />}
      {!banner.imageOnly && (
        <div className="relative z-10 flex items-center justify-center gap-2 max-w-6xl mx-auto">
          <span className="font-medium truncate min-w-0" style={{ color }}>
            {banner.title}
            {banner.subtitle && (
              <span className="ml-2 opacity-80 hidden sm:inline">{banner.subtitle}</span>
            )}
          </span>
          {banner.ctaText && banner.ctaUrl && (
            <Link
              href={banner.ctaUrl}
              className="font-semibold underline underline-offset-2 hover:opacity-80 transition-opacity whitespace-nowrap flex-shrink-0"
              style={{ color }}
            >
              {banner.ctaText} →
            </Link>
          )}
        </div>
      )}
      {dismissible && (
        <button
          className="absolute z-20 right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100 transition-opacity"
          style={{ color }}
          onClick={() => {
            const el = document.getElementById(`banner-${banner.id}`);
            if (el) el.style.display = 'none';
          }}
          aria-label={t('close')}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
